import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Download, Plus, Trash2 } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import {
  ACTION_LEVEL_LABELS,
  ACTION_STATUS_LABELS,
  generateDisciplinaryActionsWordDocument,
} from "@/lib/documentGenerator/basuWordGenerators";

interface Row {
  id: string;
  occurred_at: string | null;
  company: string | null;
  person_name: string | null;
  rule_broken: string | null;
  description: string | null;
  action_level: string;
  action_taken: string | null;
  decided_by: string | null;
  followup: string | null;
  status: string;
}

export const DisciplinaryActions: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [{ data }, { data: proj }] = await Promise.all([
      supabase
        .from("disciplinary_actions")
        .select("*")
        .eq("project_id", projectId)
        .order("occurred_at", { ascending: false }),
      supabase.from("projects").select("name").eq("id", projectId).maybeSingle(),
    ]);
    setProjectName(proj?.name ?? "");
    setRows((data as unknown as Row[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const add = async () => {
    const { error } = await supabase.from("disciplinary_actions").insert({
      project_id: projectId,
      occurred_at: new Date().toISOString().slice(0, 10),
    });
    if (error) return toast.error("Kunde inte lägga till");
    load();
  };

  const update = async (id: string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const { error } = await supabase.from("disciplinary_actions").update(patch).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("disciplinary_actions").delete().eq("id", id);
    if (error) return toast.error("Kunde inte ta bort");
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const exportWord = async () => {
    try {
      await generateDisciplinaryActionsWordDocument(rows, projectName);
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  const open = rows.filter((r) => r.status === "oppen").length;

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="46"
        exportHistory={{ documentType: "disciplinara_atgarder" }}
        title="Disciplinära åtgärder"
        description="Logg över brott mot skydds- och ordningsreglerna och vilka åtgärder som vidtagits: tillsägelse, skriftlig varning, stoppat arbete eller avvisning."
        legalRef="Projektets ordningsregler · AFS 2023:3 kap. 4"
        icon={AlertTriangle}
        meta={
          rows.length ? (
            <Badge variant={open ? "destructive" : "outline"}>
              {open ? `${open} öppna ärenden` : "Inga öppna ärenden"}
            </Badge>
          ) : null
        }
        actions={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={add}>
              <Plus className="mr-1 h-4 w-4" /> Nytt ärende
            </Button>
            {rows.length ? (
              <Button size="sm" variant="outline" onClick={exportWord}>
                <Download className="mr-1 h-4 w-4" /> Word
              </Button>
            ) : null}
          </div>
        }
      />

      {!rows.length ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Inga ärenden registrerade. Loggen används när ordningsreglerna inte följs, så att åtgärderna
            blir spårbara och konsekventa.
          </CardContent>
        </Card>
      ) : (
        rows.map((row) => (
          <Card key={row.id}>
            <CardContent className="grid gap-4 pt-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Datum</Label>
                <Input
                  type="date"
                  defaultValue={row.occurred_at ?? ""}
                  onBlur={(e) => update(row.id, { occurred_at: e.target.value || null })}
                />
              </div>
              <div className="space-y-2">
                <Label>Företag</Label>
                <Input
                  defaultValue={row.company ?? ""}
                  onBlur={(e) => update(row.id, { company: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Person (om relevant)</Label>
                <Input
                  defaultValue={row.person_name ?? ""}
                  onBlur={(e) => update(row.id, { person_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Regel som brutits</Label>
                <Input
                  defaultValue={row.rule_broken ?? ""}
                  onBlur={(e) => update(row.id, { rule_broken: e.target.value })}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Händelse</Label>
                <Textarea
                  rows={2}
                  defaultValue={row.description ?? ""}
                  onBlur={(e) => update(row.id, { description: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Åtgärdsnivå</Label>
                <Select
                  value={row.action_level}
                  onValueChange={(v) => update(row.id, { action_level: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ACTION_LEVEL_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex items-center gap-2">
                  <Select value={row.status} onValueChange={(v) => update(row.id, { status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ACTION_STATUS_LABELS).map(([k, label]) => (
                        <SelectItem key={k} value={k}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Ta bort ärendet"
                    onClick={() => remove(row.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Vidtagen åtgärd</Label>
                <Input
                  defaultValue={row.action_taken ?? ""}
                  onBlur={(e) => update(row.id, { action_taken: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Beslutad av</Label>
                <Input
                  defaultValue={row.decided_by ?? ""}
                  onBlur={(e) => update(row.id, { decided_by: e.target.value })}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Uppföljning</Label>
                <Textarea
                  rows={2}
                  defaultValue={row.followup ?? ""}
                  onBlur={(e) => update(row.id, { followup: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default DisciplinaryActions;
